import useRealtimeChannel from "@/composables/useRealtimeChannel";
import { supabaseClient } from "@/lib/database/supabase";
import { CrewRequest, CrewRequestWithAssociations } from "@shared/lib/database/query.types";
import { RealtimePostgresDeletePayload, RealtimePostgresInsertPayload, RealtimePostgresUpdatePayload } from "@supabase/supabase-js";
import { defineStore, storeToRefs } from "pinia";
import { ref, watch } from "vue";
import useAuthStore from "./useAuthStore";

export default defineStore("crewRequest", () => {
  const {
    currentPlayer,
  } = storeToRefs(useAuthStore());

  const crewRequests = ref<Array<CrewRequestWithAssociations>>([]);

  useRealtimeChannel("crew_requests", {
    handleUpsert: handleCrewRequestUpsert,
    handleDelete: handleCrewRequestDelete,
  });

  watch(currentPlayer, async () => {
    fetchCrewRequests().catch(() => {});
  }, {
    immediate: true,
  });

  function createCrewRequestQuery () {
    return supabaseClient
      .from("crew_requests")
      .select("*, crew:crews!inner(*), player:players!inner(*)");
  }

  async function fetchCrewRequests () {
    if (!currentPlayer.value?.id) return;

    await fetchCrewRequestsForPlayer(currentPlayer.value.id);
  }

  async function fetchCrewRequestsForPlayer (playerId: string) {
    const {
      data: _crewRequests,
    } = await createCrewRequestQuery()
      .neq("player_id", playerId)
      .is("accepted_at", null)
      .is("rejected_at", null)
      .order("created_at", {
        ascending: false,
      })
      .throwOnError();

    if (_crewRequests) {
      crewRequests.value = _crewRequests;
    }
  }

  async function acceptCrewRequest (crewRequest: CrewRequestWithAssociations) {
    if (!crewRequest.crew_id || !crewRequest.player_id) return;
    const {
      data: crewMembership,
    } = await supabaseClient
      .rpc("accept_crew_request", {
        crew_request_id: crewRequest.id,
      })
      .throwOnError();

    if (crewMembership) {
      removeCrewRequest(crewRequest);
    }
  }

  async function rejectCrewRequest (crewRequest: CrewRequestWithAssociations) {
    await supabaseClient
      .from("crew_requests")
      .update({
        rejected_at: "now()",
      })
      .eq("id", crewRequest.id)
      .throwOnError();

    removeCrewRequest(crewRequest);
  }

  function addOrReplaceCrewRequest (crewRequest: CrewRequestWithAssociations) {
    const existingCrewRequest = crewRequests.value.find((_crewRequest) => _crewRequest.id === crewRequest.id);

    if (existingCrewRequest) {
      Object.assign(existingCrewRequest, crewRequest);
    } else {
      crewRequests.value.unshift(crewRequest);
    }
  }

  function removeCrewRequest (crewRequest: Partial<CrewRequest>) {
    const existingIndex = crewRequests.value.findIndex((_crewRequest) => _crewRequest.id === crewRequest.id);

    if (existingIndex !== -1) {
      crewRequests.value.splice(existingIndex, 1);
    }
  }

  async function handleCrewRequestUpsert (payload: RealtimePostgresInsertPayload<CrewRequest> | RealtimePostgresUpdatePayload<CrewRequest>) {
    const {
      data: crewRequest,
    } = await createCrewRequestQuery()
      .eq("id", payload.new.id)
      .maybeSingle();

    if (crewRequest && (crewRequest.accepted_at || crewRequest.rejected_at)) {
      removeCrewRequest(crewRequest);
    } else if (crewRequest) {
      addOrReplaceCrewRequest(crewRequest);
    }
  }

  function handleCrewRequestDelete (payload: RealtimePostgresDeletePayload<CrewRequest>) {
    removeCrewRequest(payload.old);
  }

  return {
    crewRequests,
    fetchCrewRequests,
    acceptCrewRequest,
    rejectCrewRequest,
  };
});
